import { useState } from 'react';

import { useUserInfo } from '../../hooks/use-user-info';
import { getHtmlCopy } from '../../i18n';
import { getNtcSupportUrl } from '../../utilities/get-ntc-support-url';
import { List } from '../common/List';
import { CookieNoticeBanner } from '../cookie-settings/CookieNoticeBanner';
import { CookieSettingsModal } from '../cookie-settings/CookieSettingsModal';
import { GhostLoginButton } from '../ghost-login-button/GhostLoginButton';
import {
    Language,
    LanguageSelector,
} from '../language-selector/LanguageSelector';
import { FooterLink, FooterLinkSeparator } from './FooterLink';
import { FooterLinkButton } from './FooterLinkButton';

import './FooterRowPolicyLinks.scss';

const getAccessibilityUrl = (
    dyslexic: boolean,
    highContrast: boolean
): string => {
    const accessibilityUrl = new URL(
        'https://company.cdn.overdrive.com/policies/accessibility.htm'
    );
    if (highContrast) {
        accessibilityUrl.searchParams.append('high-contrast', '1');
    }
    if (dyslexic) {
        accessibilityUrl.searchParams.append('dyslexic', '1');
    }
    return accessibilityUrl.toString();
};

const getCopyrightUrl = (
    dyslexic: boolean,
    highContrast: boolean,
    locale: string
): string => {
    const copyrightUrl = new URL(
        'https://company.cdn.overdrive.com/policies/copyright.htm'
    );
    if (locale === 'fr-CA') {
        copyrightUrl.pathname = 'policies/copyright_fr_CA.htm';
    }
    if (highContrast) {
        copyrightUrl.searchParams.append('high-contrast', '1');
    }
    if (dyslexic) {
        copyrightUrl.searchParams.append('dyslexic', '1');
    }
    return copyrightUrl.toString();
};

const getPrivacyPolicyUrl = (
    dyslexic: boolean,
    highContrast: boolean
): string => {
    const privacyPolicyUrl = new URL(
        'https://company.cdn.overdrive.com/policies/privacy-policy.htm'
    );
    if (highContrast) {
        privacyPolicyUrl.searchParams.append('high-contrast', '1');
    }
    if (dyslexic) {
        privacyPolicyUrl.searchParams.append('dyslexic', '1');
    }
    return privacyPolicyUrl.toString();
};

const getPrivacyNoticeUrl = (
    dyslexic: boolean,
    highContrast: boolean
): string => {
    const privacyPolicyUrl = new URL(
        'https://company.cdn.overdrive.com/policies/datenschutzvereinbarung'
    );
    if (highContrast) {
        privacyPolicyUrl.searchParams.append('high-contrast', '1');
    }
    if (dyslexic) {
        privacyPolicyUrl.searchParams.append('dyslexic', '1');
    }
    return privacyPolicyUrl.toString();
};

export interface FooterRowPolicyLinksProps {
    advantageKey?: string;
    isAuthenticated?: boolean;
    isGdprCountry?: boolean;
    isGhostLoginEnabled?: boolean;
    languages?: Language[];
    ntcMode?: boolean;
    onLanguageChange?: (language?: Language) => void;
    openLinksInNewWindow?: boolean;
    optimizeForChina?: boolean;
    parentAccountKey?: string;
    showCookieBanner?: boolean;
    showCookieSettingsModal?: boolean;
}

export const FooterRowPolicyLinks = ({
    advantageKey,
    isAuthenticated,
    isGdprCountry,
    isGhostLoginEnabled,
    languages,
    ntcMode,
    onLanguageChange,
    openLinksInNewWindow,
    optimizeForChina,
    parentAccountKey,
    showCookieBanner,
    showCookieSettingsModal,
}: FooterRowPolicyLinksProps) => {
    const { dyslexic, highContrast, locale } = useUserInfo();
    const privacyPolicyUrl = getPrivacyPolicyUrl(dyslexic, highContrast);
    const privacyNoticeUrl = getPrivacyNoticeUrl(dyslexic, highContrast);
    const supportUrl = getNtcSupportUrl({
        libraryKey: parentAccountKey,
        advantageKey,
        dyslexic,
        highContrast,
    });
    const accessibilityUrl = getAccessibilityUrl(dyslexic, highContrast);
    const copyrightUrl = getCopyrightUrl(dyslexic, highContrast, locale);

    const [isCookieBannerOpen, setIsCookieBannerOpen] =
        useState(showCookieBanner);
    const closeCookieBanner = () => setIsCookieBannerOpen(false);

    const [isCookieSettingsModalOpen, setIsCookieSettingsModalOpen] = useState(
        showCookieSettingsModal
    );
    const closeCookieSettingsModal = () => setIsCookieSettingsModalOpen(false);
    const openCookieSettingsModal = () => setIsCookieSettingsModalOpen(true);

    const ghostLogin = !isAuthenticated && isGhostLoginEnabled;
    const copyrightYear = new Date().getFullYear();

    return (
        <>
            <div className="footer__row--bottom">
                <List
                    className="footer__secondary"
                    heading={getHtmlCopy('footer.legalLinks')}
                >
                    {ntcMode && (
                        <>
                            <FooterLink
                                href={supportUrl}
                                openInNewWindow={openLinksInNewWindow}
                                text={getHtmlCopy('getSupportCapitalized')}
                            />

                            <FooterLinkSeparator />
                        </>
                    )}

                    {locale === 'de' ? (
                        <FooterLink
                            href={privacyNoticeUrl}
                            openInNewWindow={openLinksInNewWindow}
                            text={getHtmlCopy('footerPrivacyNotice')}
                        />
                    ) : (
                        <FooterLink
                            href={privacyPolicyUrl}
                            openInNewWindow={openLinksInNewWindow}
                            text={getHtmlCopy('footerPrivacyPolicy')}
                        />
                    )}

                    <FooterLinkSeparator />

                    <FooterLinkButton
                        label={getHtmlCopy('gdpr.settingsPanel.title')}
                        onClick={openCookieSettingsModal}
                    />

                    <FooterLinkSeparator />

                    <FooterLink
                        href={accessibilityUrl}
                        openInNewWindow={openLinksInNewWindow}
                        text={getHtmlCopy('accessibilityLabel')}
                    />

                    <FooterLinkSeparator />

                    <FooterLink
                        href={copyrightUrl}
                        openInNewWindow={openLinksInNewWindow}
                        text={getHtmlCopy('footerCopyrightNotice')}
                    />

                    {optimizeForChina && (
                        <>
                            <FooterLinkSeparator />

                            <FooterLink
                                href="https://beian.miit.gov.cn/"
                                openInNewWindow
                                text="京ICP备2022006709号-4"
                            />
                        </>
                    )}

                    {optimizeForChina && (
                        <>
                            <FooterLinkSeparator />

                            <div className="footer__text">
                                {' 京公网安备11010502052603'}
                            </div>
                        </>
                    )}
                </List>

                <div className="footer__language-and-copyright">
                    {languages && languages.length > 1 && (
                        <div className="footer__language">
                            <LanguageSelector
                                id="footer-language-selector"
                                classes="footer__language-selector"
                                languages={languages ?? []}
                                onLanguageChange={onLanguageChange}
                                placement="top"
                                selectedLocale={locale}
                            />
                        </div>
                    )}

                    <div className="footer__copyright">
                        {ghostLogin && (
                            <GhostLoginButton
                                label={getHtmlCopy('footerCopyrightYear', {
                                    year: copyrightYear,
                                })}
                                slots={{
                                    button: {
                                        classes: 'footer__ghost-login-button',
                                    },
                                    form: {
                                        classes: 'footer__ghost-login-form',
                                    },
                                }}
                            />
                        )}
                        {!ghostLogin &&
                            getHtmlCopy('footerCopyrightYear', {
                                year: copyrightYear,
                            })}{' '}
                        {getHtmlCopy('footerCopyrightAllRightsReserved')}
                    </div>
                </div>
            </div>

            <CookieNoticeBanner
                openCookieSettings={openCookieSettingsModal}
                onClose={closeCookieBanner}
                isOpen={isCookieBannerOpen}
            />

            <CookieSettingsModal
                isGdprCountry={isGdprCountry}
                isDismissable={!showCookieSettingsModal}
                onClose={closeCookieSettingsModal}
                isOpen={isCookieSettingsModalOpen}
            />
        </>
    );
};
